import { Fragment, type ReactNode } from "react";

const BRANDS: { pattern: RegExp; url: string; label: string }[] = [
  { pattern: /Sunbrella(?:®)?/gi, url: "https://www.sunbrella.com", label: "Sunbrella" },
  { pattern: /Tempotest(?:\s+USA)?/gi, url: "https://www.tempotestusa.com/", label: "Tempotest" },
];

const linkClass =
  "underline decoration-red/50 underline-offset-2 transition hover:text-red hover:decoration-red";

/**
 * Wrap brand mentions (Sunbrella, Tempotest) in external links back to the
 * manufacturer. Accepts a plain string and returns a ReactNode.
 */
export function linkifyBrands(input: string): ReactNode {
  if (!input) return input;

  // Build one combined regex with named alternatives, preserving original casing.
  const combined = new RegExp(
    BRANDS.map((b) => `(${b.pattern.source})`).join("|"),
    "gi",
  );

  const parts: ReactNode[] = [];
  let lastIndex = 0;
  let match: RegExpExecArray | null;
  let key = 0;

  while ((match = combined.exec(input)) !== null) {
    const matched = match[0];
    const start = match.index;

    if (start > lastIndex) {
      parts.push(input.slice(lastIndex, start));
    }

    // Find which brand this match belongs to
    const brand =
      BRANDS.find((b) => new RegExp(`^${b.pattern.source}$`, "i").test(matched)) ??
      BRANDS[0];

    parts.push(
      <a
        key={`brand-${key++}-${start}`}
        href={brand.url}
        target="_blank"
        rel="noopener noreferrer"
        className={linkClass}
      >
        {matched}
      </a>,
    );

    lastIndex = start + matched.length;
  }

  if (lastIndex < input.length) {
    parts.push(input.slice(lastIndex));
  }

  if (parts.length === 0) return input;

  return (
    <>
      {parts.map((p, i) => (
        <Fragment key={i}>{p}</Fragment>
      ))}
    </>
  );
}
